'use client';

import React, { useState } from 'react';

interface PhotoItem {
  id?: string | number;
  url: string;
  alt?: string;
  title?: string;
}

interface PhotoSelectionProps {
  data: {
    title?: string;
    subtitle?: string;
    max_selection?: number;
    images?: PhotoItem[];
  };
}

// Високоякісні демонстраційні фотографії (Zero-Friction Default)
const DEFAULT_DEMO_PHOTOS: PhotoItem[] = [
  { id: '1', url: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80', title: 'IMG_0102.CR3' },
  { id: '2', url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=800&q=80', title: 'IMG_0105.CR3' },
  { id: '3', url: 'https://images.unsplash.com/photo-1537633552985-df8429e8048b?auto=format&fit=crop&w=800&q=80', title: 'IMG_0144.CR3' },
  { id: '4', url: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=800&q=80', title: 'IMG_0190.CR3' },
  { id: '5', url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&w=800&q=80', title: 'IMG_0215.CR3' },
  { id: '6', url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?auto=format&fit=crop&w=800&q=80', title: 'IMG_0280.CR3' },
  { id: '7', url: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=800&q=80', title: 'IMG_0312.CR3' },
  { id: '8', url: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=800&q=80', title: 'IMG_0399.CR3' },
];

export function PhotoSelectionBlock({ data }: PhotoSelectionProps) {
  const photos = data?.images && data.images.length > 0 ? data.images : DEFAULT_DEMO_PHOTOS;
  const maxSelection = data?.max_selection || 25;

  // Стан вибору: map of index -> { selected: boolean, comment: string }
  const [selections, setSelections] = useState<Record<number, { selected: boolean; comment: string }>>({});
  const [activeCommentIndex, setActiveCommentIndex] = useState<number | null>(null);

  // Модальне вікно відправки
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleFavorite = (index: number) => {
    const current = selections[index] || { selected: false, comment: '' };
    const nextSelected = !current.selected;

    // Перевірка ліміту
    const currentCount = Object.values(selections).filter((s) => s.selected).length;
    if (nextSelected && currentCount >= maxSelection) {
      alert(`Ви досягли ліміту зйомки (${maxSelection} фото). Щоб вибрати це фото, зніміть виділення з іншого.`);
      return;
    }

    setSelections({
      ...selections,
      [index]: {
        ...current,
        selected: nextSelected,
      },
    });
  };

  const updateComment = (index: number, comment: string) => {
    const current = selections[index] || { selected: true, comment: '' };
    setSelections({
      ...selections,
      [index]: {
        ...current,
        selected: true, // якщо додаємо коментар, фото автоматично обирається
        comment,
      },
    });
  };

  const selectedCount = Object.values(selections).filter((s) => s.selected).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const selectedList = Object.entries(selections)
      .filter(([_, val]) => val.selected)
      .map(([idxStr, val]) => {
        const p = photos[Number(idxStr)];
        return {
          id: p.id,
          url: p.url,
          title: p.title || `Photo_${Number(idxStr) + 1}`,
          comment: val.comment,
        };
      });

    try {
      const apiUrl = `http://${window.location.hostname}:8000/api/website/gallery-selection`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          client_name: clientName,
          client_contact: clientContact,
          gallery_title: data?.title || 'Відбір фотографій',
          selected_photos: selectedList,
        }),
      });

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.message || 'Помилка при відправці відбору.');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Не вдалося надіслати відбір. Спробуйте пізніше.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-fluid-padding max-w-7xl mx-auto px-4 relative">
      {/* Заголовок галереї */}
      <div className="text-center mb-10">
        <span className="badge-status active mb-3 inline-block">📸 Клієнтська галерея</span>
        <h1 className="heading-1 text-gray-900 mb-3">
          {data?.title || 'Весільна зйомка — Олена & Максим'}
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {data?.subtitle || 'Натисніть на сердечко, щоб додати фото в обрані для фінальної обробки, або залиште коментар до ретуші.'}
        </p>
      </div>

      {/* Сітка фотографій */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {photos.map((photo, i) => {
          const isSelected = selections[i]?.selected || false;
          const comment = selections[i]?.comment || '';

          return (
            <div
              key={photo.id || i}
              className={`group relative rounded-2xl overflow-hidden bg-gray-100 transition-all duration-300 shadow-sm ${
                isSelected ? 'ring-4 ring-blue-600 shadow-lg scale-[1.01]' : 'hover:shadow-md'
              }`}
            >
              {/* Фото */}
              <div className="aspect-[3/4] relative overflow-hidden">
                <img
                  src={photo.url}
                  alt={photo.alt || photo.title || 'Photo'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />

                {/* Градієнт оверлей */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />

                {/* Кнопка "Сердечко" (Favorites) */}
                <button
                  type="button"
                  onClick={() => toggleFavorite(i)}
                  className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-90 ${
                    isSelected
                      ? 'bg-rose-600 text-white shadow-lg scale-110'
                      : 'bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-rose-600'
                  }`}
                  title={isSelected ? 'Прибрати з обраних' : 'Додати в обрані'}
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </button>

                {/* Номер фото або назва файлу */}
                {photo.title && (
                  <span className="absolute bottom-3 left-3 text-[11px] font-mono text-white/90 bg-black/50 px-2 py-0.5 rounded backdrop-blur-sm">
                    {photo.title}
                  </span>
                )}

                {/* Кнопка швидкого коментаря */}
                <button
                  type="button"
                  onClick={() => setActiveCommentIndex(activeCommentIndex === i ? null : i)}
                  className={`absolute bottom-3 right-3 text-xs px-2.5 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm transition ${
                    comment
                      ? 'bg-blue-600 text-white font-medium'
                      : 'bg-white/80 text-gray-800 hover:bg-white'
                  }`}
                  title="Додати побажання до ретуші"
                >
                  💬 {comment ? 'Є коментар' : 'Коментар'}
                </button>
              </div>

              {/* Поле коментаря під фото (якщо розгорнуто) */}
              {(activeCommentIndex === i || comment) && (
                <div className="p-3 bg-white border-t border-gray-100">
                  <input
                    type="text"
                    placeholder="Побажання до ретуші..."
                    value={comment}
                    onChange={(e) => updateComment(i, e.target.value)}
                    className="w-full text-xs px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-600"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 🚀 Плаваючий Bottom Bar з лічильником */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-11/12 max-w-2xl">
        <div className="card-tenant bg-gray-900/95 backdrop-blur-md text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center justify-between border border-white/10">
          <div>
            <div className="text-xs text-gray-400 font-medium">Ваш вибір:</div>
            <div className="text-lg font-bold">
              ❤️ <span className="text-blue-400">{selectedCount}</span> / {maxSelection} фото
            </div>
          </div>

          <button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => setIsModalOpen(true)}
            className="btn-primary px-6 py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Надіслати фотографу ✈️
          </button>
        </div>
      </div>

      {/* Модальне вікно відправки */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ✕
            </button>

            {submitted ? (
              <div className="text-center py-6">
                <div className="text-5xl mb-3">🎉</div>
                <h3 className="heading-2 text-gray-900 mb-2">Відбір надіслано!</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Фотограф отримав список із {selectedCount} відібраних фотографій та ваші побажання до ретуші.
                </p>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSubmitted(false);
                  }}
                  className="btn-primary w-full py-3"
                >
                  Зрозуміло
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="heading-2 text-gray-900 mb-1">Підтвердження відбору</h3>
                  <p className="text-xs text-gray-500">
                    Ви відібрали {selectedCount} фото на ретуш
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 text-xs p-3 rounded-lg border border-red-200">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    Ваше ім&apos;я *
                  </label>
                  <input
                    type="text"
                    required
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Олена"
                    className="input-tenant"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    Телефон або Telegram / Instagram *
                  </label>
                  <input
                    type="text"
                    required
                    value={clientContact}
                    onChange={(e) => setClientContact(e.target.value)}
                    placeholder="@olena_inst або +38099..."
                    className="input-tenant"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full py-3.5 text-base font-semibold shadow-md mt-4"
                >
                  {submitting ? 'Відправка...' : 'Підтвердити та надіслати'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
